import { Font } from "@react-pdf/renderer";

// Font registration state
let fontsRegistered = false;
let fontRegistrationPromise: Promise<void> | null = null;

/**
 * Preloads and registers Noto Sans SC fonts for packing slips.
 * This should be called once when the app starts to cache fonts.
 * Returns a promise that resolves when fonts are ready.
 */
export const preloadPackingSlipFonts = async (): Promise<boolean> => {
  // If already registered, return immediately
  if (fontsRegistered) {
    return true;
  }

  // If registration is in progress, return the existing promise
  if (fontRegistrationPromise) {
    await fontRegistrationPromise;
    return fontsRegistered;
  }

  // Start font registration
  fontRegistrationPromise = (async () => {
    try {
      // Prefetch fonts using browser cache
      const fontUrls = [
        "https://cdn.jsdelivr.net/npm/@fontsource/noto-sans-sc@5.2.9/files/noto-sans-sc-chinese-simplified-400-normal.woff",
        "https://cdn.jsdelivr.net/npm/@fontsource/noto-sans-sc@5.2.9/files/noto-sans-sc-chinese-simplified-700-normal.woff",
      ];

      // Prefetch fonts to browser cache (non-blocking)
      fontUrls.forEach((url) => {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.as = "font";
        link.type = "font/woff";
        link.crossOrigin = "anonymous";
        link.href = url;
        document.head.appendChild(link);
      });

      // Register fonts with react-pdf (this will use cached fonts if available)
      Font.register({
        family: "NotoSansSC",
        fonts: [
          {
            src: fontUrls[0],
            fontWeight: "normal",
          },
          {
            src: fontUrls[1],
            fontWeight: "bold",
          },
        ],
      });

      fontsRegistered = true;
    } catch (error) {
      console.warn("⚠️ Failed to preload packing slip fonts:", error);
      fontsRegistered = false;
    }
  })();

  await fontRegistrationPromise;
  return fontsRegistered;
};

/**
 * Check if fonts are registered
 */
export const areFontsRegistered = (): boolean => fontsRegistered;

const CJK_RE = /[\u3040-\u30ff\u3400-\u9fff\uf900-\ufaff]/;

/** Noto Sans SC subsetting is extremely slow; only use it when the PDF actually contains CJK. */
export function textNeedsCjkFont(
  values: Array<string | number | null | undefined>
): boolean {
  return values.some((v) => v != null && CJK_RE.test(String(v)));
}
